import { PaymentPlan, PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import Styles from './PlanColumn.module.scss'
import { BillingFrequency } from 'stores/PaywallStore'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { PlanFeatureRow } from './PlanFeatureRow'
import { BlazeAI, Campaign } from 'almanac-ui/icons'
import {
  UserProfile1,
  Brand,
  LightningFilled,
  FileSearch1,
  Knowledge,
  Calendar2,
  Hash2,
  ArrowRightSquareContained,
  ArrowSkip,
  MessageText2,
  Approval,
} from 'blaze-ui/icons'
import { Iphone02, PhoneCall01, FileCheck02, Bezier01 } from 'blaze-ui/icons/16'
import { pluralize } from 'utils'
import { useMemo } from 'react'
import { PlanHeader } from './PlanHeader'
interface PlanColumnProps {
  plan: PaymentPlan
  onPlanSelect: (planKey: PaymentPlanKey) => void
  frequency: BillingFrequency.ANNUAL | BillingFrequency.MONTHLY
  discount?: boolean
}

export const PlanColumn = ({ plan, onPlanSelect, frequency, discount }: PlanColumnProps) => {
  const handlePlanSelect = () => {
    onPlanSelect(plan.key)
  }

  const planKey = plan.key

  const featureHeaderText = useMemo(() => {
    switch (plan.key) {
      case PaymentPlanKey.BlazePro:
        return 'Everything from Creator and:'
      case PaymentPlanKey.BlazeTeam:
        return 'Everything from Pro and:'
      case PaymentPlanKey.BlazeAgency:
        return 'Everything from Startup and:'
      default:
        return 'Includes:'
    }
  }, [plan.key])

  let planColor
  switch (planKey) {
    case PaymentPlanKey.BlazeCreator:
      planColor = '#4caf50'
      break
    case PaymentPlanKey.BlazePro:
      planColor = '#6c5ce7'
      break
    case PaymentPlanKey.BlazeTeam:
      planColor = '#ff4f97'
      break
    case PaymentPlanKey.BlazeAgency:
      planColor = '#e74c3c'
      break
    default:
      planColor = '#4caf50'
  }

  return (
    <div className={Styles.container}>
      {discount && (
        <div className={Styles.specialBadge}>
          <span className={Styles.crossedOut}>25%</span> <span className={Styles.boldOff}>33% OFF</span> SPECIAL
        </div>
      )}
      <PlanHeader plan={plan} frequency={frequency} />
      <div className={Styles.actionContainer}>
        <Button variant="tertiary" onPress={handlePlanSelect} fullWidth>
          Select this plan
        </Button>
      </div>
      <div className={Styles.planFeatures}>
        <Text variant="label" color="var(--dark-80)">
          {featureHeaderText}
        </Text>
        <PlanFeatureRow
          limit={plan.base_price_meta.seats_limit}
          icon={<UserProfile1 size={16} color={planColor} />}
          feature={pluralize('Seat', parseInt(`${plan.base_price_meta.seats_limit}`) || 0)}
        />
        <PlanFeatureRow
          limit={plan.base_price_meta.brand_kits_limit}
          icon={<Brand size={16} color={planColor} />}
          feature={'Brand Kits'}
        />
        <PlanFeatureRow
          limit={plan.base_price_meta.integrations_limit}
          icon={<Iphone02 size={16} color={planColor} />}
          feature={'Accounts per platform'}
        />
        <PlanFeatureRow
          limit={plan.base_price_meta.published_posts_limit}
          icon={<LightningFilled size={16} color={planColor} />}
          feature={'Published posts per platform'}
        />
        <PlanFeatureRow
          limit={
            plan.base_price_meta.ai_words_limit === 'unlimited'
              ? 'Unlimited'
              : `${parseInt(`${plan.base_price_meta.ai_words_limit}`) / 1000}K`
          }
          icon={<BlazeAI size={16} color={planColor} />}
          feature={'AI Words'}
        />
        <PlanFeatureRow
          limit={plan.base_price_meta.plagiarism_checks_limit}
          icon={<FileSearch1 size={16} color={planColor} />}
          feature={'Plagiarism Scans'}
        />
        <PlanFeatureRow
          limit={plan.base_price_meta.source_materials_limit}
          icon={<Knowledge size={16} color={planColor} />}
          feature={'Source Materials'}
        />

        {/* Extra features based on plan */}
        {planKey === PaymentPlanKey.BlazeCreator && (
          <>
            <PlanFeatureRow icon={<PhoneCall01 size={16} color={planColor} />} feature={'Unlimited docs & designs'} />
            <PlanFeatureRow icon={<UserProfile1 size={16} color={planColor} />} feature={'Unlimited guest access'} />
            <PlanFeatureRow icon={<Calendar2 size={16} color={planColor} />} feature={'Social media calendar'} />
            <PlanFeatureRow icon={<Campaign size={16} color={planColor} />} feature={'Multi-channel campaign'} />
            <PlanFeatureRow icon={<FileCheck02 size={16} color={planColor} />} feature={'Unlimited SEO checks'} />
          </>
        )}

        {planKey === PaymentPlanKey.BlazePro && (
          <>
            <PlanFeatureRow icon={<Hash2 size={16} color={planColor} />} feature={'Link & hashtag banks'} />
            <PlanFeatureRow
              icon={<ArrowRightSquareContained size={16} color={planColor} />}
              feature={'Multiple post wizards'}
            />
            <PlanFeatureRow icon={<PhoneCall01 size={16} color={planColor} />} feature={'Priority support'} />
            <PlanFeatureRow icon={<ArrowSkip size={16} color={planColor} />} feature={'Priority AI processing'} />
          </>
        )}

        {planKey === PaymentPlanKey.BlazeTeam && (
          <>
            <PlanFeatureRow
              icon={<MessageText2 size={16} color={planColor} />}
              feature={'Real time collaboration, Comments & Tasks'}
            />
            <PlanFeatureRow icon={<Approval size={16} color={planColor} />} feature={'Content approval tools'} />
          </>
        )}

        {planKey === PaymentPlanKey.BlazeAgency && (
          <>
            <PlanFeatureRow
              icon={<PhoneCall01 size={16} color={planColor} />}
              feature={'Dedicated onboarding & workspace setup'}
            />
            <PlanFeatureRow
              icon={<Bezier01 size={16} color={planColor} />}
              feature={'Multi-workspace volume discounts'}
            />
          </>
        )}
      </div>
    </div>
  )
}
